import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download } from "lucide-react";
import {
  getCardDrilldown, money, formatMethod, formatStatus, toCsv, downloadCsv,
  type CardDrilldown,
} from "@/lib/financialLedger";

interface Props {
  card: string | null;
  period: string;
  range?: { start_date?: string; end_date?: string };
  onClose: () => void;
}

export function CardDrilldownDialog({ card, period, range, onClose }: Props) {
  const [data, setData] = useState<CardDrilldown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!card) { setData(null); return; }
    setLoading(true); setError(null);
    getCardDrilldown(card, period, range)
      .then(setData)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, period, range?.start_date, range?.end_date]);

  return (
    <Dialog open={!!card} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data?.label ?? "Where this number comes from"}</DialogTitle>
          <DialogDescription>{data?.explain ?? "Loading the transactions behind this figure…"}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Pulling every transaction behind this figure…
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {data && !loading && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant="secondary">{data.rows.length} transaction{data.rows.length === 1 ? "" : "s"}</Badge>
              {data.total_cents !== null && (
                <span className="font-medium">Total: {money(data.total_cents)}</span>
              )}
              <span className="text-xs text-muted-foreground">
                {data.period.start_date} – {data.period.end_date}
              </span>
            </div>

            {data.engine_backed ? (
              <p className="text-sm text-muted-foreground">
                This figure is calculated by the payroll and financial engines from scheduled classes, not from posted
                cash transactions. It is an obligation or projection, never counted as money already moved.
              </p>
            ) : data.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions contribute to this figure in this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">In</TableHead>
                      <TableHead className="text-right">Out</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs whitespace-nowrap">{r.transaction_date}</TableCell>
                        <TableCell className="text-xs max-w-[280px]"><span className="block truncate">{r.description}</span></TableCell>
                        <TableCell className="text-xs">{r.category}</TableCell>
                        <TableCell className="text-right text-xs">{r.direction === "in" ? money(r.amount_cents) : ""}</TableCell>
                        <TableCell className="text-right text-xs">{r.direction === "out" ? money(r.amount_cents) : ""}</TableCell>
                        <TableCell className="text-xs">{formatMethod(r.payment_method)}</TableCell>
                        <TableCell className="text-xs">{formatStatus(r.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {data && data.rows.length > 0 && (
            <Button
              variant="outline"
              onClick={() => downloadCsv(`silver-sage-${data.card}-${data.period.start_date}-to-${data.period.end_date}.csv`, toCsv(data.rows))}
            >
              <Download className="h-4 w-4 mr-1" /> Export these transactions
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
